import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useMidApplications } from "hooks/crm/useMidApplications";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import {
  MID_APP_STATUS,
  statusToneFrom,
  midAppInFlightCount,
  openPendCount,
} from "utilities/statusTones";
import SearchPanel from "components/SearchPanel";
import SelectCompanyModal from "components/MidApplications/SelectCompanyModal";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import SourceBadge from "components/SourceBadge";
import RelativeTime from "components/RelativeTime";
import RecordTable from "components/Records/RecordTable";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import {
  Filter,
  FileText,
  Building2,
  AlertTriangle,
  CheckCircle,
  Plus,
  Eye,
} from "lucide-react";

const appTone = statusToneFrom(MID_APP_STATUS);

// SOUND-688: `openPendCount` moved to utilities/statusTones next to the derived
// pended STATE it feeds, so the list, the detail page and the state badge cannot
// disagree about what an open pend is. It used to be defined here.
const APP_FILTER_FIELDS = filterFieldsFor("midApplications");

function MidApplicationsInner() {
  const { filters, set } = useSearch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-updatedAt");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectCompanyOpen, setSelectCompanyOpen] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const internal = isInternal(auth.user);

  const { applications, pagination, summary, loading, error, refetch } = useMidApplications({
    filters,
    page,
    limit,
    sort,
  });

  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, APP_FILTER_FIELDS);
  // Boolean-shaped: "true" or unset. Unset means "don't filter", not "no pends".
  const pendsOnly = filters.hasPends === "true";

  const columns = useMemo(
    () => [
      {
        key: "client",
        exportValue: (app) => app.clientName || app.companyName || "",
        width: "20%",
        header: "Client",
        cell: (app) => (
          <div className="truncate font-medium">
            {app.clientName || app.companyName || "—"}
          </div>
        ),
      },
      {
        key: "dbaName",
        width: "19%",
        header: "DBA",
        // SOUND-654: sorted server-side via a $lookup through the
        // application->dba edge. Sorting on a dbaId would order by UUID.
        sortKey: "dbaName",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        cell: (app) => app.dbaName || "—",
      },
      {
        key: "pends",
        exportValue: openPendCount,
        width: "13%",
        header: "Pends",
        // Derived server-side, so it sorts like any stored field. Descending
        // first — the reason to sort by pends is to surface what is blocked.
        sortKey: "unresolvedPendCount",
        defaultSortDir: "desc",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        cell: (app) => {
          const unresolved = openPendCount(app);
          return unresolved > 0 ? (
            <span className="badge badge-warning">{unresolved} unresolved</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        key: "processorName",
        width: "14%",
        header: "Processor",
        // SOUND-654: resolved from processorId; no name is stored.
        sortKey: "processorName",
        cell: (app) => app.processorName || "—",
      },
      {
        key: "source",
        exportValue: (app) => app.source?.type || "",
        width: "12%",
        header: "Source",
        cell: (app) => <SourceBadge source={app.source} />,
      },
      {
        key: "status",
        exportValue: (app) => MID_APP_STATUS[app.currentStatus]?.label || app.currentStatus || "",
        width: "12%",
        header: "Status",
        sortKey: "currentStatus",
        className: "w-36",
        // SOUND-688: NO pended badge here. The Pends column two cells over
        // already says "3 unresolved" — a second mark on the same row is
        // redundant, and it truncated to "Pe…" at this column width (CPO 2026-08-24).
        cell: (app) => <RecordStatusPill status={app.currentStatus} toneFor={appTone} />,
      },
      {
        key: "updatedAt",
        exportValue: (app) => app.updatedAt || "",
        width: "10%",
        header: "Updated",
        sortKey: "updatedAt",
        defaultSortDir: "desc",
        headerClassName: "hidden lg:table-cell w-32",
        className: "hidden lg:table-cell text-muted-foreground",
        cell: (app) => <RelativeTime value={app.updatedAt} />,
      },
    ],
    []
  );

  return (
    <div>
      <PageHeader
        title="Applications"
        subtitle="Track and prioritize merchant applications across processors."
        meta={[{ label: "Total", value: (pagination.total || 0).toLocaleString() }]}
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="badge badge-brand ml-1">{activeFilterCount}</span>
              )}
            </button>
            {internal && (
              <button className="btn btn-primary" onClick={() => setSelectCompanyOpen(true)}>
                <Plus className="h-4 w-4" />
                Create Application
              </button>
            )}
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          items={[
            {
              label: "Total Applications",
              value: pagination.total || 0,
              tone: "brand",
              loading,
              icon: <FileText />,
            },
            {
              label: "Unresolved Pends",
              value: summary.unresolvedPends ?? 0,
              tone: "warning",
              loading,
              icon: <AlertTriangle />,
            },
            {
              // Every non-terminal status, not just `in_progress` — see
              // MID_APP_IN_FLIGHT. Keying on the one status undercounted by
              // ~110 once out_for_signature/submitted/underwriting existed.
              label: "In Flight",
              value: midAppInFlightCount(summary.statusCounts),
              tone: "info",
              loading,
              icon: <Building2 />,
            },
            {
              label: "Approved",
              value: summary.statusCounts?.approved ?? 0,
              tone: "success",
              loading,
              icon: <CheckCircle />,
            },
          ]}
        />

        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder="Search client, DBA, or processor"
        >
          {/* SOUND-688 — the pended STATE as a filter, sitting left of the stage
              dropdown because it is a different question: Status asks WHERE an
              application is, this asks WHETHER anything is outstanding on it.
              A toggle, not a select — the only useful value is "has some", since
              "has none" is what the unfiltered list already shows. */}
          <button
            type="button"
            onClick={() => set("hasPends", pendsOnly ? "" : "true")}
            className={`btn ${pendsOnly ? "btn-secondary" : "btn-outline"}`}
            aria-pressed={pendsOnly}
          >
            <AlertTriangle className="h-4 w-4" />
            Has pends
          </button>
          <select
            className="select w-auto"
            value={filters.status || ""}
            onChange={(e) => set("status", e.target.value)}
            aria-label="Status"
          >
            <option value="">Status: Any</option>
            {Object.entries(MID_APP_STATUS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {/* SOUND-690 — the Focus board. A NAVIGATION target, not a filter:
              it has its own route so a standup link is a link, and it renders a
              different shape entirely (tickets + pends grouped by client), not
              this list narrowed. Internal only. */}
          {internal && (
            <Link to="/mid-applications/focus" className="btn btn-outline">
              <Eye className="h-4 w-4" />
              Focus
            </Link>
          )}
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={applications}
          rowKey={(app) => app.applicationId}
          onRowClick={(app) => navigate(`/mid-applications/${app.applicationId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={activeFilterCount > 0}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          noMatch={{ icon: FileText, heading: "No matching applications" }}
          empty={{
            icon: FileText,
            heading: "No applications in pipeline",
            description: "No applications available.",
            errorText: "Failed to load applications.",
            action: internal ? (
              <button className="btn btn-primary" onClick={() => setSelectCompanyOpen(true)}>
                <Plus className="h-4 w-4" />
                Create Application
              </button>
            ) : null,
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      <SearchPanel
        namespace="midApplications"
        title="Filter Applications"
        // Driven inline by the "Has pends" toggle; a second control for the
        // same filter in the drawer could disagree with it.
        hiddenFields={["hasPends"]}
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />

      {selectCompanyOpen && internal && (
        <SelectCompanyModal
          onClose={() => setSelectCompanyOpen(false)}
          onSelect={(company) => {
            setSelectCompanyOpen(false);
            navigate(`/companies/${company.companyId}/create`);
          }}
        />
      )}
    </div>
  );
}

function MidApplications() {
  return (
    <SearchProvider namespace="midApplications">
      <MidApplicationsInner />
    </SearchProvider>
  );
}

export default requireAuth(MidApplications);
